// Ask Witty owns login and signup on the classic site. Anything that used to
// send a guest to /login raises an auth intent here instead; the Ask Witty
// sidecar (AskWittyFab) hosts the verification and the page never navigates.
//
// The /login and /signup routes are only redirect shims. They never own the
// customer login form; the sidecar or full-page chat renders the auth card.

export const ASK_WITTY_AUTH_INTENT_EVENT = "pw-askwitty-auth-intent";

// The sidecar is the only auth host that follows the user around, and it only
// mounts on the desktop dashboard. Below this width the sole host is the
// full-page chat, so a redirect shim has to send guests there or the pending
// intent lands on a page with nothing to answer it.
export const ASK_WITTY_SIDECAR_MEDIA_QUERY = "(min-width:1100px)";
export const ASK_WITTY_CHAT_PATH = "/askwitty";

export function askWittySidecarViewport() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia(ASK_WITTY_SIDECAR_MEDIA_QUERY).matches;
}

export function isAskWittyCheckoutPath(path) {
  return /^\/(?:checkout|table-booking|package-booking|partywitty-pay|payment-now|my-bookings-details)(?:[/?#]|$)/.test(path);
}

// Where a guest must land for the stashed intent to be picked up.
export function askWittyAuthLandingPath(returnTo = "/") {
  if (askWittySidecarViewport() && !isAskWittyCheckoutPath(returnTo)) return returnTo;
  return String(returnTo).startsWith(ASK_WITTY_CHAT_PATH) ? returnTo : ASK_WITTY_CHAT_PATH;
}

// ponytail: module-level flag, not context — the sidecar is a singleton and the
// callers (navbar, feed cards, route guards) share no provider with it.
let hostAvailable = false;

function withIntentId(detail = {}) {
  const source = detail && typeof detail === "object" ? detail : {};
  return {
    ...source,
    intentId: String(source.intentId || `auth-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
  };
}

export function registerAskWittyAuthHost(available) {
  hostAvailable = Boolean(available);
}

export function askWittyAuthHostAvailable() {
  return hostAvailable;
}

// Handoff for callers that still hard-navigate to /login or /signup: the intent has to
// outlive the redirect, because the sidecar only mounts on the page we land on.
const PENDING_KEY = "pw_askwitty_auth_intent_v1";

export function hasPendingAskWittyAuthIntent() {
  try { return Boolean(window.sessionStorage.getItem(PENDING_KEY)); } catch { return false; }
}

export function stashAskWittyAuthIntent(detail = {}) {
  try { window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(withIntentId(detail))); } catch {}
}

export function consumeAskWittyAuthIntent() {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(PENDING_KEY);
    window.sessionStorage.removeItem("pw_last_classic_path_v1");
    return JSON.parse(raw) || {};
  } catch {
    return null;
  }
}

// Returns false when no chat host is listening. Callers can then use the
// /login redirect shim, which returns to a route where Ask Witty is mounted.
export function requestAskWittyAuth(detail = {}) {
  if (!hostAvailable || typeof window === "undefined") return false;
  window.dispatchEvent(
    new CustomEvent(ASK_WITTY_AUTH_INTENT_EVENT, { detail:withIntentId(detail) }),
  );
  return true;
}
